import { IPassportGroup } from '@/pages/MDM/mdm.types'
import useTreeSelectCtrl from '@/shared/Tree/ctrl/useTreeSelectCtrl'
import domUtils from '@/utils/dom.utils'
import { useEffect, useRef, useState } from 'react'
import EquipmentPassportTree from '../../EquipmentFormFields/EquipmentPassportTree'
import useChangedGroups from '../../EditEquipmentPassportSidePage/hooks/useChangedPassportGroup'
import usePassportDifferences from '../../EditEquipmentPassportSidePage/hooks/usePassportDifferences'
import css from '../equipmentPassportVersions.module.css'
import useHttpLoader from '@/shared/hooks/useHttpLoader'
import { IEquipment, IEquipmentPassport, IPrevEquipmentPassportVersion } from '@/pages/Equipment/equipment.types'
import equipmentApi from '@/pages/Equipment/equipment.api'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import useEquipmentPassport from '@/pages/Equipment/hooks/useEquipmentPassport'
import EmptyPlaceholder from '@/shared/Common/EmptyPlaceholder'
import passportVersionsConstants from '../passportVersions.constants'
import EquipmentPassportСomparisonView from '../../../Passport/EquipmentPassportСomparisonView'

interface IProps {
  prevPassportVersionId: IPrevEquipmentPassportVersion['id']
  passportVersionId: IPrevEquipmentPassportVersion['id']
  equipment: IEquipment
  isChangedOnly: boolean
}

const EquipmentPassportVersionsСomparisonContent = (props: IProps) => {
  const { passport: currentPassportInstance } = useEquipmentPassport(props.equipment?.passportInstanceId)
  const [passportInstances, setPassportInstances] = useState<{
    prevPassportInstance: IEquipmentPassport
    passportInstance: IEquipmentPassport
  }>({
    prevPassportInstance: null,
    passportInstance: null,
  })
  const { wait, loading } = useHttpLoader()

  const groupSelectCtrl = useTreeSelectCtrl<IPassportGroup['id']>(
    `/equipment/lifts/passportVersions/isVersionComparisonEnabled`,
    null,
    'regularState'
  )

  useEffect(() => {
    const requestPrevPassportData = async () => {
      return equipmentApi.getHistoricalPassportData(props.equipment.id, props.prevPassportVersionId)
    }

    const requestPassportData = async () => {
      return equipmentApi.getHistoricalPassportData(props.equipment.id, props.passportVersionId)
    }

    const job = async () => {
      const summaryResp = {
        status: 'success',
        body: { prevPassportInstance: null, passportInstance: null },
      }

      if (props.prevPassportVersionId !== passportVersionsConstants.currentVersionId) {
        const resp = await requestPrevPassportData()
        if (resp.status === 'success') summaryResp.body['prevPassportInstance'] = resp.body.passportInstance
        else return resp
      }

      if (props.passportVersionId !== passportVersionsConstants.currentVersionId) {
        const resp = await requestPassportData()
        if (resp.status === 'success') summaryResp.body['passportInstance'] = resp.body.passportInstance
        else return resp
      }

      return summaryResp
    }

    wait(job(), (resp) => {
      if (resp.status === 'success') {
        setPassportInstances({
          prevPassportInstance: resp.body.prevPassportInstance ?? currentPassportInstance,
          passportInstance: resp.body.passportInstance ?? currentPassportInstance,
        })
      }
    })
  }, [props.prevPassportVersionId, props.equipment, props.passportVersionId, currentPassportInstance])

  const { passportDifferences, changedPassportSchemaFieldIds } = usePassportDifferences({
    prevPassportValues: passportInstances.prevPassportInstance?.values,
    passportValues: passportInstances.passportInstance?.values,
  })

  const changedGroups = useChangedGroups({
    passportSchemaId: currentPassportInstance?.passportId,
    changedPassportSchemaFieldIds: changedPassportSchemaFieldIds,
  })

  const tableRef = useRef<HTMLDivElement>(null)

  const handleGroupSelect = (groupId: IPassportGroup['id']) => {
    const targetElement = tableRef.current?.querySelector(`#groupId_${groupId}`) as HTMLElement
    domUtils.scrollToTargetElement(targetElement)
    groupSelectCtrl.handleSelect(groupId)
  }

  const renderContent = () => {
    if (loading || !passportInstances.passportInstance || !passportInstances.prevPassportInstance) {
      return (
        <div className="flex-lines center height100 width100">
          <ThreeDotsLoader inscription="Загрузка..." />
        </div>
      )
    }
    if (!Object.values(passportDifferences).some((item) => item.isChanged)) {
      return (
        <div className="flex-lines center height100 width100">
          <EmptyPlaceholder style={{ alignSelf: 'center' }} inscription="Различий между версиями нет" />
        </div>
      )
    }
    return (
      <>
        <div className={css.passportNavBlock}>
          <EquipmentPassportTree
            selectCtrl={groupSelectCtrl}
            passportSchemaId={currentPassportInstance.passportId}
            handleSelect={handleGroupSelect}
            visibleGroups={props.isChangedOnly ? changedGroups.map((item) => item.id) : null}
          />
        </div>
        <EquipmentPassportСomparisonView
          passportDifferences={passportDifferences}
          passportSchemaId={currentPassportInstance.passportId}
          ref={tableRef}
          onGroupIntoViewportChange={groupSelectCtrl.handleSelect}
          changedGroups={changedGroups}
          isChangedOnly={props.isChangedOnly}
        />
      </>
    )
  }

  return (
    <div
      className="inline-flex-gap gap24 height100"
      style={{ minHeight: 0, overflow: 'auto', flex: '1 1 0%', paddingRight: '18px' }}
    >
      {renderContent()}
    </div>
  )
}

export default EquipmentPassportVersionsСomparisonContent
